import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { NotificationManager } from "react-notifications";
import cleanDeep from "clean-deep";

import { Actions as OVERTIMEACTIONS } from "@/redux/actions/overtimeActions";
import { Actions as ALLOWANCEACTIONS } from "@/redux/actions/allowanceActions";
import { Actions as TEMPLATEACTIONS } from "@/redux/actions/templateActions";
import { Actions as ADVISORACTIONS } from "@/redux/actions/advisorActions";

import NoItemsRow from "@/components/common/NoItemsRow";
import OvertimeRow from "@/components/company-settings/overtime-tab/OvertimeRow";
import AllowanceRow from "../../company-settings/allowance-tab/AllowanceRow";
import TemplateRow from "./TemplateRow";

import { CheckCircleIcon, PlusIcon } from "@heroicons/react/20/solid";

const AdvisorPayroll = ({
	overtimes,
	allowances,
	templates,
	jurisdiction,
	advisorId,
	addOvertime,
	addAllowance,
	addTemplate,
	updateAdvisorDetails,
}) => {
	const { handleSubmit } = useForm();

	const onSubmit = () => {
		const invalidAllowances = allowances?.find(
			(allowance) => !(allowance.name && allowance.method && allowance.hasOwnProperty("taxable")),
		);

		const invalidOvertimes = overtimes?.find(
			(overtime) => !(overtime.name && overtime.rate && overtime.hasOwnProperty("isToil")),
		);

		const invalidTemplates =
			templates?.length > 0 &&
			templates?.find(
				(template) => !(template.name && (template?.overtimes?.length > 0 || template?.allowances?.length > 0)),
			);

		if (invalidAllowances)
			return NotificationManager.error("Please ensure all fields are filled before saving!", "Allowances");
		if (invalidOvertimes)
			return NotificationManager.error("Please ensure all fields are filled before saving!", "Overtime");
		if (invalidTemplates)
			return NotificationManager.error("Please ensure all fields are filled before saving!", "Templates");

		updateAdvisorDetails({
			id: advisorId,
			detailsToUpdate: cleanDeep({
				allowances: allowances?.map((allowance) => {
					allowance.temporary = undefined;
					return allowance;
				}),
				overtimes: overtimes?.map((overtime) => {
					overtime.temporary = undefined;
					return overtime;
				}),
				templates: templates?.map((template) => {
					return template;
				}),
			}),
		});
	};
	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			style={{ paddingBottom: 30 }}
		>
			<div className="form-section col-xs-12">
				<div className="col-xs-12">
					<div className="pull-left">
						<h4>Overtime & Special Rates</h4>
					</div>
					<button
						className="btn btn-success pull-right"
						type="button"
						id="add-overtime-type"
						onClick={() => {
							addOvertime();
						}}
					>
						<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
						<div style={{ float: "left" }}>Add Rate</div>
					</button>
				</div>
				<div className="clearfix" />

				<div className="col-xs-12 pl-0 pb-6">
					{overtimes?.length ? (
						<>
							{overtimes?.map((overtime) => (
								<OvertimeRow
									key={overtime.id}
									overtime={overtime}
									advisor={true}
								/>
							))}
						</>
					) : (
						<table
							id="table-timesheets"
							className="js-table-sections table table-hover"
						>
							<NoItemsRow name="Overtime or Special Rates" />
						</table>
					)}
				</div>
			</div>
			<div className="form-section col-xs-12">
				<div className="col-xs-12">
					<div className="pull-left">
						<h4>{jurisdiction == "UK" ? "Pay Items" : "Allowances"}</h4>
					</div>
					<button
						className="btn btn-success pull-right"
						type="button"
						id="add-allowance-type"
						onClick={() => {
							addAllowance();
						}}
					>
						<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
						<div style={{ float: "left" }}>Add {jurisdiction == "UK" ? "Pay Item" : "Allowances"}</div>
					</button>
				</div>
				<div className="col-xs-12 pl-0 pb-6">
					{allowances?.length ? (
						<>
							{allowances?.map((allowance) => (
								<AllowanceRow
									key={allowance.id}
									allowance={allowance}
									advisor={true}
								/>
							))}
						</>
					) : (
						<table
							id="table-timesheets"
							className="js-table-sections table table-hover"
						>
							<NoItemsRow name={jurisdiction == "UK" ? "Pay Items" : "Allowances"} />
						</table>
					)}
				</div>
			</div>
			<div className="form-section col-xs-12">
				<div className="col-xs-12">
					<div className="pull-left">
						<h4>Templates</h4>
					</div>
					<button
						className="btn btn-success pull-right"
						type="button"
						id="add-template-type"
						onClick={() => {
							addTemplate();
						}}
					>
						<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
						<div style={{ float: "left" }}>Add Template</div>
					</button>
				</div>
				<div className="clearfix" />

				<div className="col-xs-12 pl-0 pb-6">
					{templates?.length ? (
						<>
							{templates?.map((template) => (
								<TemplateRow
									key={template.id}
									template={template}
								/>
							))}
						</>
					) : (
						<table
							id="table-timesheets"
							className="js-table-sections table table-hover"
						>
							<NoItemsRow name="Templates" />
						</table>
					)}
				</div>
			</div>
			<div className="clearfix" />
			<div className="col-xs-12 pr-0 pull-right">
				<button
					className="btn btn-success pull-right"
					type="submit"
				>
					<CheckCircleIcon style={{ height: "1.5em", float: "left" }} />
					<div style={{ float: "left " }}>Save</div>
				</button>
			</div>
		</form>
	);
};

const mapStateToProps = (state) => {
	return {
		jurisdiction: state.advisors?.settings?.jurisdiction,
		overtimes: state.advisors?.settings?.overtimes,
		allowances: state.advisors?.settings?.allowances,
		templates: state.advisors?.settings?.templates,
		advisorId: state.my.profile.advisorId,
	};
};

const mapDispatchToProps = (dispatch) => ({
	addOvertime: () => dispatch(OVERTIMEACTIONS.addAdvisorOvertime()),
	addAllowance: () => dispatch(ALLOWANCEACTIONS.addAdvisorAllowance()),
	addTemplate: () => dispatch(TEMPLATEACTIONS.addTemplate()),
	updateAdvisorDetails: ({ id, detailsToUpdate }) =>
		dispatch(ADVISORACTIONS.updateAdvisorDetails({ id, detailsToUpdate })),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdvisorPayroll);
